import { NavigationGuard } from 'vue-router'
import Vue, { getCurrentInstance, nextTick, provide } from 'vue'
import NP from 'nprogress'
import { useAuth } from '@/plugins/auth/src'
import { loadUserSetting } from '@/plugins/auth'
import router from './index'
import { changeRole } from '@/api/home'
import { useHttp } from '@/plugins/http/src/index'
import { NOTICE_PATHS, EXHIBITION_PATHS, NO_ACCOUNT_FEATURE_PATHS } from './constants'
import {
  hasSimulatedLoginSession,
  isShareAppPath,
  isSimulatedLoginUser,
  loginByShareCompanyCode,
  readCompanyCode,
  redirectSimulatedUserToPasswordLogin,
  simulatedDashboardEntry
} from '@/plugins/auth/simulated-login'
import {
  clearSimulatedLoginSession,
  simulatedRouteDecision,
  syncSimulatedLoginSession
} from '@/plugins/auth/simulated-login-session'

const whiteList = ['/login', '/error/**'] // ant style wildcat

export const tempTokenList = [
  ...NOTICE_PATHS,
  '/dashboard/ar',
  '/dashboard/asp',
  '/dashboard/ct',
  ...EXHIBITION_PATHS,
  ...NO_ACCOUNT_FEATURE_PATHS
]

const frontGuard: NavigationGuard = function(to, from, next) {
  NP.start()
  const { auth } = useAuth()

  const blockSimulatedRoute = (path: string, fullPath: string): boolean => {
    const decision = simulatedRouteDecision(path)
    if (decision === 'allow') return false
    if (decision === 'enter-dashboard') {
      next(simulatedDashboardEntry(auth))
      return true
    }
    redirectSimulatedUserToPasswordLogin(fullPath)
    next(false)
    NP.done()
    return true
  }

  const enforceSimulatedRoute = (path: string, fullPath: string, done: () => void) => {
    const decide = () => {
      if (auth.authenticated) syncSimulatedLoginSession(auth.principle?.user)
      if (!isSimulatedLoginUser(auth)) {
        done()
        return
      }
      if (!blockSimulatedRoute(path, fullPath)) done()
    }

    if (!auth.authenticationToken) {
      clearSimulatedLoginSession()
      done()
      return
    }
    if (auth.authenticated) {
      decide()
      return
    }
    auth
      .load()
      .then(() => decide())
      .catch(() => {
        if (hasSimulatedLoginSession() && simulatedRouteDecision(path) === 'password-login') {
          redirectSimulatedUserToPasswordLogin(fullPath)
          next(false)
          NP.done()
          return
        }
        done()
      })
  }

  if (isShareAppPath(to.path)) {
    const companyCode = readCompanyCode(to.query.companyCode)
    if (companyCode) {
      loginByShareCompanyCode(companyCode)
        .then(() => next())
        .catch((err: any) => {
          const status = err?.status || err?.response?.status
          const code = err?.data?.code ?? err?.response?.data?.code
          if (status === 401 || code === 401 || code === 1100 || code === 1101) {
            next(false)
            NP.done()
            return
          }
          next()
        })
      return
    }
  }

  const _url = new URL(window.location.href)
  const path = _url.pathname
  const guidanceToken = _url.searchParams.get('guidance')
  const news = _url.searchParams.get('news')

  // 临时token白名单,优先验证
  if (onTempTokenList(to.path)) {
    const exhibitionToken = _url.searchParams.get('exhibitionToken')
    if (exhibitionToken) {
      // 展览用户， 验证展览用户
      if (auth.isExhibitionUser(exhibitionToken) && EXHIBITION_PATHS.includes(path)) {
        auth.exhibitionToken = exhibitionToken
        loadUserSetting().then(() => {
          next()
        })
        return
      } else {
        auth.exhibitionToken = ''
        window.location.assign('/login')
      }
    }

    const notice = _url.searchParams.get('notice')
    // for notice related page
    if (notice && NOTICE_PATHS.includes(path)) {
      auth.notice = notice
      loadUserSetting().then(() => {
        next()
      })
      return
    }

    if (onList(NO_ACCOUNT_FEATURE_PATHS, path)) {
      // other validations should be added in its related page
      console.log('process.env.VUE_APP_STATIC_AUTH_TOKEN', process.env.VUE_APP_STATIC_AUTH_TOKEN)
      auth.mockToken = process.env.VUE_APP_STATIC_AUTH_TOKEN || ''
      loadUserSetting().then(() => {
        next()
      })
      return
    }

    if (guidanceToken) {
      // 指引用户， 验证指引用户
      localStorage.setItem('authentication', JSON.stringify({ token: guidanceToken }))
      _url.searchParams.delete('guidance')
      window.history.replaceState({}, '', _url.toString())
    }
  }

  // 白名单或未设置权限， 不验证
  if (onWhiteList(to.path) || !to.meta?.pid) {
    if (
      !onWhiteList(to.path) &&
      !isShareAppPath(to.path) &&
      (hasSimulatedLoginSession() || isSimulatedLoginUser(auth))
    ) {
      enforceSimulatedRoute(to.path, to.fullPath, () => next())
      return
    }
    next()
    return
  }

  if (auth.authenticated && isSimulatedLoginUser(auth)) {
    if (!blockSimulatedRoute(to.path, to.fullPath)) next()
    return
  }

  const authControl = () => {
    // 权限控制
    auth
      .access(to.meta?.pid as string)
      .then((pass: boolean) => {
        syncSimulatedLoginSession(auth.principle?.user)
        if (isSimulatedLoginUser(auth)) {
          if (!blockSimulatedRoute(to.path, to.fullPath)) {
            if (to.query.guidance) {
              delete to.query.guidance
              next(to)
            } else {
              next()
            }
          }
          return
        }
        if (pass || to.meta?.pid === '/') {
          if (['', '/'].includes(to.path)) {
            if (auth.accessSync('/home')) {
              next('/home')
            } else if (auth.accessSync('/org/company/detail')) {
              next('/org/company/detail')
            } else {
              next({ path: '/error/403' })
            }
          } else {
            if (to.query.guidance) {
              delete to.query.guidance
              next(to)
            } else {
              next()
            }
          }
        } else {
          next({ path: '/' })
          NP.done()
        }
      })
      .catch(({ data }) => {
        const code = data?.code

        const needLogin = code === 1101 || code === 1100 || (auth.notice && code === 1002)
        if (needLogin) {
          window.location.assign('/login')
        }
        NP.done()
      })
  }

  const { http } = useHttp()
  if (news && guidanceToken && http) {
    // if news has value, means we need to change role to supplier to allow user to access the dashboard ar page\
    http
      .get('/api/air8-auth-center/userClient/queryUserInfoByUserId')
      .then(async (res: any) => {
        if (res.companyRole === 'COMPANY') {
          await changeRole('SUPPLIER')
        }
      })
      .finally(() => {
        authControl()
      })
  } else {
    authControl()
  }
}

export function onList(list: string[], path: string) {
  const convertRegEx = (expr: string) => {
    return new RegExp(
      '^' +
        (expr || '')
          .replace(/\*\*/g, '#__#')
          .replace(/\*/g, '[^/]*')
          .replace(/#__#/g, '.*') +
        '$'
    )
  }

  return list.some(v => convertRegEx(v).test(path))
}

function onWhiteList(path: string) {
  return onList(whiteList, path)
}

export function onTempTokenList(path: string) {
  return onList(tempTokenList, path)
}

export default frontGuard
